/**
 * Derived organizations — a small synchronous registry of the org descriptors fetched at boot.
 *
 * An org generated by SEPortal is a variant of a built-in one: it declares `baselineOrg` and a
 * map of display-string `labels`. Several places in this app resolve behaviour from a
 * compile-time table keyed by org id — the scenario registry, the autonomous-agent boot
 * payloads, and the CEO persona / sensitive-label / currency maps. A derived org matches none
 * of them on its own, so it would have no scenarios (every "build it by describing it" moment
 * dying into the no-match fallback), no boot payload (its sentinel rendering as an empty
 * shell), and no CEO board.
 *
 * Those lookups are synchronous and outside React — `findScenario` is called from
 * `DemoGateway.converse`, which cannot await — so the descriptors are stashed here once, by the
 * same boot code that fetches them for the org switcher.
 *
 * Everything degrades to today's behaviour when nothing is registered or an org declares no
 * baseline, which is the case for all eight built-in orgs.
 */
import type { OrgDescriptor, OrgId } from '@wand/shared';

let DESCRIPTORS: Map<OrgId, OrgDescriptor> = new Map();

/** Called once at boot with the fetched org registry. */
export function setOrgDescriptors(orgs: readonly OrgDescriptor[]): void {
  DESCRIPTORS = new Map(orgs.map((o) => [o.id, o]));
}

export function orgDescriptor(org: OrgId): OrgDescriptor | undefined {
  return DESCRIPTORS.get(org);
}

/** The org `org` derives from, if any. */
export function baselineOf(org: OrgId): OrgId | undefined {
  const baseline = DESCRIPTORS.get(org)?.baselineOrg;
  return baseline && baseline !== org ? baseline : undefined;
}

/**
 * The ids to try when resolving something keyed by org: the org itself, then its baseline.
 * Always at least one entry, so callers can loop unconditionally.
 */
export function orgChain(org: OrgId): OrgId[] {
  const baseline = baselineOf(org);
  return baseline ? [org, baseline] : [org];
}

/**
 * Rewrite the baseline's vocabulary into this org's, for text that is authored at compile time
 * and so cannot be re-labelled in the data — scenario prose and the agent-build scripts.
 *
 * Longest match first, so "Clause Analyst" is not half-rewritten by a rule for "Clause". Only
 * whole words are replaced, so a label for "NDA" cannot corrupt "standard".
 */
export function applyLabels(text: string, org: OrgId): string {
  const labels = DESCRIPTORS.get(org)?.labels;
  if (!labels || text.length === 0) return text;

  let out = text;
  for (const from of Object.keys(labels).sort((a, b) => b.length - a.length)) {
    const to = labels[from];
    if (to === undefined || from.length === 0) continue;
    out = out.split(from).join(to);
  }
  return out;
}

/** True when this org has any substitutions at all — lets hot paths skip the work. */
export function hasLabels(org: OrgId): boolean {
  const labels = DESCRIPTORS.get(org)?.labels;
  return labels !== undefined && Object.keys(labels).length > 0;
}

/** Test seam: drop everything registered. */
export function resetOrgDescriptors(): void {
  DESCRIPTORS = new Map();
}
